"use client";

import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";
import useSWR from "swr";
import { getApiUrl } from "@/utils/api";
import { fetcher } from "@/utils/fetch";
import { getToken, handleSessionExpiry, isTokenExpired } from "@/utils/auth";
import { AdminPermission, Permissions, UIPermission } from "@/utils/permissions";
import { NavItem } from "@/components/Sidebar";
import { getCurrentVariant, VARIANTS } from "@/config/variants";

export type Region = {
    region_id: number;
    long_description: string;
    short_description: string;
};

export type Franchise = {
    franchise_id: number;
    franchise_name: string;
    luxury: boolean;
    truck: boolean;
};

export type PositionGroup = {
    position_group_id: number;
    position_group_description: string;
};

export type StaticMetadata = {
    car_job_titles_per_car_department: {
        [key: string]: string[];
    };
    truck_job_titles_per_truck_department: {
        [key: string]: string[];
    };
    regions: Array<Region>;
    franchises: Array<Franchise>;
    /** Dashboard study year resolved by the backend (e.g. 2026). */
    study_year: number;
    /** Calendar year the data covers (study_year - 1, e.g. 2025). */
    data_year: number;
};

export type PeerGroupInfo = {
    peer_group_criteria: string;
    peer_group_display_order: number;
    peer_group_long_description: string;
    peer_group_short_description: string;
    peer_group_default: boolean;
    type?: string;
};

export type ParticipantInfo = {
    nada_member_id: string;
    participant_name: string;
    luxury: string;
    car_truck: string;
    state: string;
    zip_code: string;
    volume: string;
    avg_comp: string;
    three_yr_ret: string;
    an_turnover: string;
    peer_groups: {
        [key: string]: PeerGroupInfo;
    };
    group_peer_groups: {
        [key: string]: PeerGroupInfo;
    };
};

export type UserInfo = {
    group_id: string | null;
    participant_id: string | null;
    first_name: string;
    last_name: string;
    email: string;
    permissions: Permissions;
};

export type GroupInfo = {
    group_id: string;
    car_truck: string;
    group_name: string;
    peer_groups_table: {
        [key: string]: PeerGroupInfo;
    };
    peer_groups_charts: {
        [key: string]: PeerGroupInfo;
    };
};

interface JobTitlesResponse {
    [department: string]: {
        [position: string]: number;
    };
}

type DealershipContextType = {
    metadata: StaticMetadata | undefined;
    participantInfo: ParticipantInfo | undefined;
    groupInfo: GroupInfo | undefined;
    userInfo: UserInfo | undefined;
    jobTitlesData: JobTitlesResponse | undefined;
    positionGroups: PositionGroup[] | undefined;
    selectedPeerGroup: string;
    setSelectedPeerGroup: (group: string) => void;
    // Department and Position state
    selectedDepartment: string;
    selectedPositionsIds: number[];
    setSelectedDepartment: (department: string) => void;
    setSelectedPositionsIds: (positionsIds: number[]) => void;
    isJobTitlesLoading: boolean;
    isMetadataLoading: boolean;
    isParticipantLoading: boolean;
    isGroupInfoLoading: boolean;
    isUserInfoLoading: boolean;
    isPositionGroupsLoading: boolean;
    metadataError: Error | undefined;
    participantError: Error | undefined;
    groupInfoError: Error | undefined;
    userInfoError: Error | undefined;
    jobTitlesError: Error | undefined;
    positionGroupsError: Error | undefined;
    printing: boolean;
    setPrinting: (printing: boolean) => void;
    filteredDealerReportingItems: NavItem[];
    filteredGroupReportingItems: NavItem[];
    filteredStandaloneItems: NavItem[];
    filteredMiddleStandaloneItems: NavItem[];
};

const DealershipContext = createContext<DealershipContextType | undefined>(undefined);

export function DealershipProvider({ children }: { children: ReactNode }) {
    const [selectedPeerGroup, setSelectedPeerGroup] = useState<string>("");

    const [printing, setPrinting] = useState(false);

    // Department and Position state
    const [selectedDepartment, setSelectedDepartment] = useState<string>("All Positions");
    const [selectedPositionsIds, setSelectedPositionsIds] = useState<number[]>([-1]);

    // Check if JWT token exists - only fetch when authenticated
    const [shouldFetch, setShouldFetch] = useState(false);

    useEffect(() => {
        // Only fetch when we hold a token that hasn't expired. Treating an expired
        // token as unauthenticated stops us firing requests we know will 401.
        const checkToken = () => {
            const jwtToken = typeof window !== "undefined" ? getToken() : undefined;
            setShouldFetch(!!jwtToken && !isTokenExpired(jwtToken));
        };

        checkToken();

        // Optionally listen for storage events (if token is set in another tab/window)
        const handleStorageChange = () => checkToken();
        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    // Proactively re-auth an idle page (ES-1030): the token can lapse while the
    // user sits on a dashboard, after which AuthWrapper (which doesn't revalidate)
    // still shows the page but every data fetch 401s into "No data available".
    // If we had a session but the token is now gone or expired, bounce to login —
    // on tab focus/visibility for an instant response, plus a slow interval as a
    // backstop for a tab that stays foregrounded.
    useEffect(() => {
        if (!shouldFetch) return;

        const checkSession = () => {
            const jwtToken = getToken();
            if (!jwtToken || isTokenExpired(jwtToken)) {
                handleSessionExpiry();
            }
        };

        const onVisible = () => {
            if (document.visibilityState === "visible") checkSession();
        };

        window.addEventListener("focus", checkSession);
        document.addEventListener("visibilitychange", onVisible);
        const interval = setInterval(checkSession, 60 * 1000);

        return () => {
            window.removeEventListener("focus", checkSession);
            document.removeEventListener("visibilitychange", onVisible);
            clearInterval(interval);
        };
    }, [shouldFetch]);

    const {
        data: metadata,
        error: metadataError,
        isLoading: isMetadataLoading,
    } = useSWR<StaticMetadata>(shouldFetch ? getApiUrl("/static_metadata") : null, fetcher);

    const {
        data: participantInfo,
        error: participantError,
        isLoading: isParticipantLoading,
    } = useSWR<ParticipantInfo>(shouldFetch ? getApiUrl("/participant_info") : null, fetcher);

    const {
        data: groupInfo,
        error: groupInfoError,
        isLoading: isGroupInfoLoading,
    } = useSWR<GroupInfo>(shouldFetch ? getApiUrl("/group_info") : null, fetcher);

    const {
        data: userInfo,
        error: userInfoError,
        isLoading: isUserInfoLoading,
    } = useSWR<UserInfo>(shouldFetch ? getApiUrl("/user_info") : null, fetcher);

    const {
        data: jobTitlesData,
        error: jobTitlesError,
        isLoading: isJobTitlesLoading,
    } = useSWR<JobTitlesResponse>(
        shouldFetch && participantInfo?.car_truck
            ? getApiUrl(`/job_titles?car_truck=${participantInfo.car_truck}`)
            : null,
        fetcher
    );

    const {
        data: positionGroups,
        error: positionGroupsError,
        isLoading: isPositionGroupsLoading,
    } = useSWR<PositionGroup[]>(
        shouldFetch && participantInfo?.car_truck
            ? getApiUrl(`/position_groups?car_truck=${participantInfo.car_truck}`)
            : null,
        fetcher
    );

    const dealerReportingItems: NavItem[] = [
        {
            text: "executiveSummary.title",
            path: "/executive-summary",
            permission: "executive_summary",
            className: "exec-step-1",
        },
        { text: "compensation.title", path: "/compensation", permission: "compensation", className: "" },
        { text: "turnover.sidebarTitle", path: "/turnover", permission: "turnover", className: "" },
        {
            text: "workSchedule.title",
            path: "/schedule",
            permission: "hours_and_benefits",
            className: "",
        },
        { text: "ui.navigation.benefits", path: "/benefits", permission: "hours_and_benefits", className: "" },
        {
            text: "ui.navigation.changeDealership",
            path: "/dealership-selector",
            permission: "compensation",
            className: "",
        },
    ];

    const groupReportingItems: NavItem[] = [
        {
            text: "ui.navigation.introduction",
            path: "/group/introduction",
            permission: "intro_group",
            className: "",
        },
        {
            text: "executiveSummary.title",
            path: "/group/executive-summary",
            permission: "executive_summary_group",
            className: "exec-step-1",
        },
        {
            text: "compensation.title",
            path: "/group/compensation",
            permission: "compensation_group",
            className: "",
        },
        {
            text: "turnover.sidebarTitle",
            path: "/group/turnover",
            permission: "turnover_group",
            className: "",
        },
        {
            text: "workSchedule.title",
            path: "/group/schedule",
            permission: "hours_and_benefits_group",
            className: "",
        },
        {
            text: "ui.navigation.benefits",
            path: "/group/benefits",
            permission: "hours_and_benefits_group",
            className: "",
        },
        {
            text: "demographics.title",
            path: "/group/demographics",
            permission: "demographics_group",
            className: "",
        },
        {
            text: "ui.navigation.changeGroup",
            path: "/group/group-selector",
            permission: "compensation_group",
            className: "",
        },
    ];

    const standaloneItems: NavItem[] = [
        { text: "ui.navigation.queryTool", path: "/query-tool", permission: "report_builder", className: "" },
        {
            text: "ui.navigation.trendsReport",
            path: "/trend-report",
            permission: "trend_report",
            className: "trends-step-1",
        },
        { text: "faq.title", path: "/faq", permission: "faq", className: "faq-step-1" },
        {
            text: "ui.navigation.userManagement",
            path: "/admin/users",
            permission: "user_management",
            className: "",
        },
        {
            text: "ui.navigation.payrollManagement",
            path: "/admin/payroll",
            permission: "payroll_management",
            className: "",
        },
        {
            text: "ui.navigation.participationReport",
            path: "/admin/participation-report",
            permission: "participation_report",
            className: "",
        },
        {
            text: "ui.navigation.sqlQueryTool",
            path: "/admin/sql-query",
            permission: "sql_query_tool",
            className: "",
        },
        {
            text: "ui.navigation.systemAdmin",
            path: "/admin/system-admin",
            permission: "system_admin",
            className: "",
        },
    ];

    const middleStandaloneItems: NavItem[] = [
        {
            text: "ui.navigation.dealerGroupBenchmarking",
            path: "/dealer-group-benchmarking",
            permission: "dealer_group_benchmarking",
            className: "",
        },
    ];

    // CADA has no group reporting and no equivalent dealer groups in Canada
    // (confirmed for the 2026 launch / by Troy Meier), so the group reporting
    // tabs and the Dealer Group Benchmarking page are not offered on CADA.
    // (middleware.ts also redirects any /group/* URL to its standalone page.)
    const isCadaVariant = getCurrentVariant() === VARIANTS.CADA;

    const filteredDealerReportingItems = dealerReportingItems.filter(
        (item) =>
            userInfo?.permissions?.ui?.includes(item.permission as UIPermission) ||
            userInfo?.permissions?.admin?.includes(item.permission as AdminPermission)
    );

    const filteredGroupReportingItems = isCadaVariant
        ? []
        : groupReportingItems.filter(
              (item) =>
                  userInfo?.permissions?.ui?.includes(item.permission as UIPermission) ||
                  userInfo?.permissions?.admin?.includes(item.permission as AdminPermission)
          );

    const filteredStandaloneItems = standaloneItems.filter(
        (item) =>
            userInfo?.permissions?.ui?.includes(item.permission as UIPermission) ||
            userInfo?.permissions?.admin?.includes(item.permission as AdminPermission)
    );

    const filteredMiddleStandaloneItems = middleStandaloneItems.filter(
        (item) =>
            !(isCadaVariant && item.path === "/dealer-group-benchmarking") &&
            (userInfo?.permissions?.ui?.includes(item.permission as UIPermission) ||
                userInfo?.permissions?.admin?.includes(item.permission as AdminPermission))
    );

    return (
        <DealershipContext.Provider
            value={{
                metadata,
                participantInfo,
                groupInfo,
                userInfo,
                selectedPeerGroup,
                setSelectedPeerGroup,
                // Department and Position state
                selectedDepartment,
                selectedPositionsIds,
                setSelectedDepartment,
                setSelectedPositionsIds,
                // Helper functions
                isMetadataLoading,
                isParticipantLoading,
                isGroupInfoLoading,
                isUserInfoLoading,
                metadataError,
                participantError,
                groupInfoError,
                userInfoError,
                jobTitlesData,
                isJobTitlesLoading,
                jobTitlesError,
                positionGroups,
                isPositionGroupsLoading,
                positionGroupsError,
                printing,
                setPrinting,
                filteredDealerReportingItems,
                filteredGroupReportingItems,
                filteredStandaloneItems,
                filteredMiddleStandaloneItems,
            }}
        >
            {children}
        </DealershipContext.Provider>
    );
}

export function useDealership() {
    const context = useContext(DealershipContext);
    if (context === undefined) {
        throw new Error("useDealership must be used within a DealershipProvider");
    }
    return context;
}
